import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PDFUploadPanel from './components/PDFUploadPanel';
import PDFEditorCanvas from './components/PDFEditorCanvas';
import LotEditorPanel from './components/LotEditorPanel';
import { floorPlanService } from "../../services/floorPlanService";

import pdfService from "../../services/pdfService";
import openaiService from "../../services/openaiService";

const AdminFloorPlanEditor = () => {
  const { user } = useAuth();
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [floorPlanData, setFloorPlanData] = useState(null);
  const [selectedLot, setSelectedLot] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const handlePDFUpload = async (file) => {
    try {
      setError('');
      setSuccess('');

      if (!file) return setError('No se seleccionó ningún archivo');
      if (file?.type !== 'application/pdf') return setError('El archivo debe ser un PDF');

      if (pdfUrl) URL.revokeObjectURL(pdfUrl);

      setPdfFile(file);
      const url = URL.createObjectURL(file);
      setPdfUrl(url);

      setSuccess('PDF cargado exitosamente.');

      setFloorPlanData(null);
      setSelectedLot(null);
      setAnnotations([]);
    } catch (err) {
      console.error('PDF upload error:', err);
      setError(err?.message || 'Error al cargar el PDF');
    }
  };

  const normalizeAnalysis = (raw) => {
    return {
      sections: raw?.sections || [],
      lots: (raw?.lots || [])?.map((lot) => ({
        number: lot?.number || 'N/A',
        area: lot?.area || 0,
        dimensions: lot?.dimensions || 'N/A',
        location: lot?.location || 'N/A',
        status: lot?.status || 'available',
        coordinates: lot?.coordinates || { x: 0, y: 0, width: 0, height: 0 },
      })),
      confidence: raw?.confidence || 0,
    };
  };

const handleAnalyzeWithAI = async () => {
  if (!pdfFile) return setError("Primero debes cargar un PDF");

  setAnalyzing(true);
  setError("");
  setSuccess("");

  // ---------------------------
  // Helpers (retry / throttle)
  // ---------------------------
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const parseEdgeStatus = (err) => {
    const msg = String(err?.message || "");
    const m = msg.match(/Edge Error \((\d+)\)/);
    return m ? Number(m[1]) : null;
  };

  const shouldRetry = (status, msg) => {
    if (!status) return false;
    if (status === 429) return true; // rate limit
    if (status === 546) return true; // WORKER_LIMIT
    if (status >= 500) return true;
    // a veces 400 por payload grande -> no reintentar
    if (status === 413) return false;
    // si OpenAI falló por transient
    if (String(msg || "").includes("WORKER_LIMIT")) return true;
    return false;
  };

  const callEdgeWithRetry = async (payload, opts = {}) => {
    const maxAttempts = opts.maxAttempts ?? 5;
    const baseDelay = opts.baseDelay ?? 700;

    let lastErr;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await openaiService.analyzePDFFloorPlan(payload);
      } catch (err) {
        lastErr = err;
        const status = parseEdgeStatus(err);
        const msg = err?.message || "";

        if (!shouldRetry(status, msg) || attempt === maxAttempts) {
          throw err;
        }

        // backoff exponencial + jitter
        const jitter = Math.floor(Math.random() * 350);
        const delay = Math.min(9000, baseDelay * Math.pow(2, attempt - 1) + jitter);

        console.warn(`Retry ${attempt}/${maxAttempts} (status=${status}) en ${delay}ms`);
        await sleep(delay);
      }
    }
    throw lastErr;
  };

  // ---------------------------
  // Merge + coords
  // ---------------------------
  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  // Convierte coords (0-100 del tile) a coords (0-100 global)
  const toGlobalCoords = (coords, row, col, rows, cols) => {
    const x = Number(coords?.x ?? 0);
    const y = Number(coords?.y ?? 0);
    const w = Number(coords?.width ?? 0);
    const h = Number(coords?.height ?? 0);

    const tileW = 100 / cols;
    const tileH = 100 / rows;

    const gx = col * tileW + (x / 100) * tileW;
    const gy = row * tileH + (y / 100) * tileH;
    const gw = (w / 100) * tileW;
    const gh = (h / 100) * tileH;

    return {
      x: clamp(gx, 0, 100),
      y: clamp(gy, 0, 100),
      width: clamp(gw, 0, 100),
      height: clamp(gh, 0, 100),
    };
  };

  // Dedupe simple por “caja cercana” (evita duplicados entre tiles)
  const dedupeLots = (lots) => {
    const out = [];
    for (const lot of lots) {
      const c = lot?.coordinates || {};
      const cx = (c.x ?? 0) + (c.width ?? 0) / 2;
      const cy = (c.y ?? 0) + (c.height ?? 0) / 2;

      // umbral: si un centro está muy cerca, lo consideramos el mismo
      const dup = out.find((o) => {
        const oc = o?.coordinates || {};
        const ocx = (oc.x ?? 0) + (oc.width ?? 0) / 2;
        const ocy = (oc.y ?? 0) + (oc.height ?? 0) / 2;
        const dx = Math.abs(cx - ocx);
        const dy = Math.abs(cy - ocy);
        return dx < 0.7 && dy < 0.7; // ajustable
      });

      if (!dup) out.push(lot);
    }
    return out;
  };

  // ---------------------------
  // 1) Primero intenta PDF base64
  // ---------------------------
  try {
    try {
      const pdfBase64 = await pdfService.pdfToBase64(pdfFile);
      const data = await callEdgeWithRetry({ pdfBase64 }, { maxAttempts: 3, baseDelay: 700 });
      const normalized = normalizeAnalysis(data);

      if ((normalized?.lots?.length || 0) >= 60) {
        setFloorPlanData(normalized);
        setSuccess(`Análisis completado (PDF): ${normalized?.lots?.length || 0} lotes detectados`);
        return;
      } else {
        console.warn("PDF devolvió pocos lotes; se usará TILING para capturar la mayoría.");
      }
    } catch (e) {
      console.warn("Falló modo pdfBase64, pasaré a TILING:", e);
    }

    // ---------------------------
    // 2) TILING
    // ---------------------------
    if (typeof pdfService.pdfToImageTiles !== "function") {
      throw new Error(
        "No existe pdfService.pdfToImageTiles. Agrega esa función (tiling) al pdfService.js."
      );
    }

    // Configuración recomendada para tu plano (800+ lotes):
    // - 6x6 o 7x7, scale 1.35–1.6
    const { tiles } = await pdfService.pdfToImageTiles(pdfFile, {
      rows: 6,
      cols: 6,
      scale: 1.45,
      format: "jpeg",
      quality: 0.72,
    });

    if (!tiles?.length) throw new Error("No se generaron tiles del PDF.");

    // Concurrencia para no saturar Edge/OpenAI
    const CONCURRENCY = 2;
    const GAP_BETWEEN_CALLS_MS = 250;

    let allLots = [];
    let allSections = [];
    let confidences = [];

    let idx = 0;
    const worker = async () => {
      while (idx < tiles.length) {
        const current = tiles[idx++];
        const { row, col, rows, cols, imageDataUrl } = current;

        // throttle suave
        await sleep(GAP_BETWEEN_CALLS_MS);

        // llamada con retry
        const result = await callEdgeWithRetry(
          { imageDataUrl },
          { maxAttempts: 5, baseDelay: 900 }
        );

        // normaliza
        const normalized = normalizeAnalysis(result);

        // ajustar coords a global 0-100
        const adjustedLots = (normalized?.lots || []).map((lot) => ({
          ...lot,
          coordinates: toGlobalCoords(lot?.coordinates, row, col, rows, cols),
        }));

        allLots.push(...adjustedLots);
        allSections.push(...(normalized?.sections || []));
        if (typeof normalized?.confidence === "number") confidences.push(normalized.confidence);

        // Progreso visible
        setSuccess(
          `Analizando por tiles... (${Math.min(idx, tiles.length)}/${tiles.length}) | lotes acumulados: ${allLots.length}`
        );
      }
    };

    // Ejecutar pool de workers
    const workers = Array.from({ length: CONCURRENCY }, () => worker());
    await Promise.all(workers);

    // Deduplicar lotes
    allLots = dedupeLots(allLots);

    // Confidence promedio
    const confidence =
      confidences.length ? confidences.reduce((a, b) => a + b, 0) / confidences.length : 0;

    // Merge de sections (simple)
    // (en planos grandes, sections del modelo pueden variar; aquí solo agregamos)
    const merged = {
      sections: allSections,
      lots: allLots,
      confidence,
    };

    const normalizedFinal = normalizeAnalysis(merged);
    setFloorPlanData(normalizedFinal);
    setSuccess(`Análisis completado (TILING): ${normalizedFinal?.lots?.length || 0} lotes detectados`);
  } catch (err) {
    console.error("AI analysis error:", err);
    setError(err?.message || "Error al analizar el PDF con IA");
  } finally {
    setAnalyzing(false);
  }
};




  const handleLotUpdate = (lotId, updates) => {
    if (!floorPlanData?.lots) return;

    setFloorPlanData((prev) => ({
      ...prev,
      lots: prev?.lots?.map((lot) => (lot?.number === lotId ? { ...lot, ...updates } : lot)),
    }));

    if (selectedLot?.number === lotId) {
      setSelectedLot((prev) => ({ ...prev, ...updates }));
    }
  };

  const calculateGroupedCoordinates = (lots) => {
    if (!lots?.length) return { x: 0, y: 0, width: 0, height: 0 };
    const valid = lots?.filter((l) => l?.coordinates);
    if (!valid?.length) return { x: 0, y: 0, width: 0, height: 0 };

    const minX = Math.min(...valid?.map((l) => l?.coordinates?.x));
    const minY = Math.min(...valid?.map((l) => l?.coordinates?.y));
    const maxX = Math.max(...valid?.map((l) => l?.coordinates?.x + l?.coordinates?.width));
    const maxY = Math.max(...valid?.map((l) => l?.coordinates?.y + l?.coordinates?.height));

    return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
  };

  const handleLotGroup = (lotIds) => {
    if (!floorPlanData?.lots?.length || !lotIds?.length) return;

    const lotsToGroup = floorPlanData?.lots?.filter((lot) => lotIds?.includes(lot?.number));
    if (!lotsToGroup?.length) return;

    const groupedLot = {
      number: lotIds?.join('-'),
      area: lotsToGroup?.reduce((sum, lot) => sum + (lot?.area || 0), 0),
      dimensions: 'Agrupado',
      location: lotsToGroup?.[0]?.location,
      coordinates: calculateGroupedCoordinates(lotsToGroup),
      status: 'available',
      isGrouped: true,
      groupedLots: lotIds,
    };

    setFloorPlanData((prev) => ({
      ...prev,
      lots: [...prev?.lots?.filter((lot) => !lotIds?.includes(lot?.number)), groupedLot],
    }));

    setSuccess(`${lotIds?.length} lotes agrupados exitosamente`);
  };

  const handleSaveFloorPlan = async () => {
  if (!floorPlanData?.lots?.length) return setError("No hay datos para guardar");
  if (!pdfFile) return setError("Primero debes cargar un PDF");

  try {
    setSaving(true);
    setError("");
    setSuccess("");

    // ✅ 1) Subir PDF real a Storage (URL permanente)
    const uploadedPdfUrl = await floorPlanService.uploadFloorPlanPDF(pdfFile);

    // ✅ 2) Generar preview de la 1ra página como JPG (Blob)
    if (typeof pdfService.pdfToPreviewImage !== "function") {
      throw new Error(
        "Falta pdfService.pdfToPreviewImage(pdfFile). Agrega esa función a src/services/pdfService.js"
      );
    }
    const previewBlob = await pdfService.pdfToPreviewImage(pdfFile);

    // ✅ 3) Subir preview a Storage (URL permanente)
    const uploadedPreviewUrl = await floorPlanService.uploadPreviewImage(previewBlob);

    // ✅ 4) Guardar registro en BD con URLs reales
    const saved = await floorPlanService.saveFloorPlanToDB({
      projectName: "Residencial 3 Volcanes",
      sections: floorPlanData?.sections || [],
      lots: floorPlanData?.lots || [],
      pdfUrl: uploadedPdfUrl,
      previewUrl: uploadedPreviewUrl,
    });

    setSuccess("✅ Plano guardado exitosamente en producción");

    console.log("Saved floor plan:", saved);
  } catch (err) {
    console.error("Save error:", err);
    setError(err?.message || "Error al guardar el plano");
  } finally {
    setSaving(false);
  }
};

  const breadcrumbs = [
    { label: 'Dashboard', path: '/user-dashboard' },
    { label: 'Editor de Planos', path: '/admin-floor-plan-editor' },
  ];

  return (
    <>
      <Helmet>
        <title>Editor de Planos - Admin | Residencial 3 Volcanes</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <div className="px-4 md:px-6 lg:px-8 py-4 sm:py-6 max-w-[1920px] mx-auto">
          <BreadcrumbTrail items={breadcrumbs} />

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
            <div>
              <h1 className="text-2xl sm:text-3xl font-heading font-bold text-foreground mb-2">
                Editor de Planos PDF
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground mt-[30px] mb-0">
                Carga un PDF del plano y usa IA para detectar y editar lotes
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto mt-[70px]">
              <Button
                onClick={handleAnalyzeWithAI}
                disabled={!pdfFile || analyzing}
                variant="outline"
              >
                {analyzing ? (
                  <>
                    <Icon name="Loader2" size={18} className="animate-spin mr-2" />
                    Analizando...
                  </>
                ) : (
                  <>
                    <Icon name="Sparkles" size={18} className="mr-2" />
                    Analizar con IA
                  </>
                )}
              </Button>

              <Button onClick={handleSaveFloorPlan} disabled={!floorPlanData || saving}>
                {saving ? (
                  <>
                    <Icon name="Loader2" size={18} className="animate-spin mr-2" />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={18} className="mr-2" />
                    Guardar Plano
                  </>
                )}
              </Button>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg flex items-start gap-3">
              <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <p className="text-error text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-success/10 border border-success rounded-lg flex items-start gap-3">
              <Icon name="CheckCircle" size={20} className="text-success flex-shrink-0 mt-0.5" />
              <p className="text-success text-sm">{success}</p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <PDFUploadPanel onUpload={handlePDFUpload} pdfFile={pdfFile} />

              {floorPlanData && (
                <LotEditorPanel
                  lots={floorPlanData?.lots}
                  selectedLot={selectedLot}
                  onLotSelect={setSelectedLot}
                  onLotUpdate={handleLotUpdate}
                  onLotGroup={handleLotGroup}
                />
              )}
            </div>

            <div className="lg:col-span-3">
              <PDFEditorCanvas
                pdfUrl={pdfUrl}
                floorPlanData={floorPlanData}
                selectedLot={selectedLot}
                onLotSelect={setSelectedLot}
                annotations={annotations}
                onAnnotationsChange={setAnnotations}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminFloorPlanEditor;